import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { CreatePlan, UpdatePlan } from './SubscriptionSlice';

const PlanModal = ({ show, onClose, plan }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    plan_name: '',
    description: '',
    price: '',
    student_limit: '',
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (plan) {
      setFormData({
        plan_name: plan.plan_name || '',
        description: plan.description || '',
        price: plan.price || '',
        student_limit: plan.student_limit || '',
        is_active: plan.is_active !== undefined ? plan.is_active : true
      });
    }
  }, [plan]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.plan_name.trim()) {
      newErrors.plan_name = 'Plan name is required';
    }
    if (!formData.price || parseFloat(formData.price) < 0) {
      newErrors.price = 'Valid price is required';
    }
    if (!formData.student_limit || parseInt(formData.student_limit) < 0) {
      newErrors.student_limit = 'Valid student limit is required';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const result = plan
        ? await dispatch(UpdatePlan({ id: plan.id, data: formData }))
        : await dispatch(CreatePlan(formData));

      if (result.payload?.code === 1) {
        toast.success(plan ? 'Plan updated successfully' : 'Plan created successfully');
        onClose(true);
      } else {
        toast.error(result.payload?.message || 'Operation failed');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={() => onClose(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{plan ? 'Edit Plan' : 'Create Plan'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Plan Name *</Form.Label>
            <Form.Control
              type="text"
              name="plan_name"
              value={formData.plan_name}
              onChange={handleChange}
              isInvalid={!!errors.plan_name}
              placeholder="Enter plan name"
            />
            <Form.Control.Feedback type="invalid">
              {errors.plan_name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter plan description"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Price (£) *</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              name="price"
              value={formData.price}
              onChange={handleChange}
              isInvalid={!!errors.price}
              placeholder="0.00"
            />
            <Form.Control.Feedback type="invalid">
              {errors.price}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Student Limit *</Form.Label>
            <Form.Control
              type="number"
              name="student_limit"
              value={formData.student_limit}
              onChange={handleChange}
              isInvalid={!!errors.student_limit}
              placeholder="Enter student limit"
            />
            <Form.Control.Feedback type="invalid">
              {errors.student_limit}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              name="is_active"
              label="Active"
              checked={formData.is_active}
              onChange={handleChange}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => onClose(false)}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Saving...' : plan ? 'Update' : 'Create'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PlanModal;
